import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './PersonalAppSection.css';

const PersonalAppSection: React.FC = () => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const sectionRef = useRef<HTMLElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [activePhoneIndex, setActivePhoneIndex] = useState(1); // Default to center phone (main phone)
  
  // Check if section content overflows viewport on mobile
  useEffect(() => {
    const checkScrollable = () => {
      if (sectionRef.current && window.innerWidth <= 768) {
        const sectionHeight = sectionRef.current.scrollHeight;
        const viewportHeight = window.innerHeight;
        setIsScrollable(sectionHeight > viewportHeight);
      } else {
        setIsScrollable(false);
      }
    };
    
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    
    return () => {
      window.removeEventListener('resize', checkScrollable);
    };
  }, []);
  
  // Scroll to center phone (main phone) on mobile load
  useEffect(() => {
    const scrollToCenter = () => {
      if (carouselRef.current && window.innerWidth <= 768) {
        const carousel = carouselRef.current;
        const centerPhone = carousel.querySelector('.main-phone') as HTMLElement;
        if (centerPhone) {
          const phoneOffset = centerPhone.offsetLeft;
          const carouselWidth = carousel.offsetWidth;
          const phoneWidth = centerPhone.offsetWidth;
          const scrollLeft = phoneOffset - (carouselWidth / 2) + (phoneWidth / 2);
          
          setTimeout(() => {
            carousel.scrollTo({
              left: scrollLeft,
              behavior: 'smooth'
            });
          }, 500); // Delay to ensure animation has started
        }
      }
    };
    
    scrollToCenter();
    window.addEventListener('resize', scrollToCenter);
    
    return () => {
      window.removeEventListener('resize', scrollToCenter);
    };
  }, [inView]);
  
  // Detect which phone is centered during scroll with smooth transitions
  useEffect(() => {
    const handleCarouselScroll = () => {
      if (carouselRef.current && window.innerWidth <= 768) {
        // Use requestAnimationFrame for smooth updates
        requestAnimationFrame(() => {
          const carousel = carouselRef.current;
          if (!carousel) return;
          
          const phones = carousel.querySelectorAll('.phone-mockup');
          const carouselRect = carousel.getBoundingClientRect();
          const carouselCenter = carouselRect.left + carouselRect.width / 2;
          
          let closestPhoneIndex = 1; // Default to center phone
          let minDistance = Infinity;
          
          phones.forEach((phone, index) => {
            const phoneRect = phone.getBoundingClientRect();
            const phoneCenter = phoneRect.left + phoneRect.width / 2;
            const distance = Math.abs(phoneCenter - carouselCenter);
            
            if (distance < minDistance) {
              minDistance = distance;
              closestPhoneIndex = index;
            }
          });
          
          if (closestPhoneIndex !== activePhoneIndex) {
            setActivePhoneIndex(closestPhoneIndex);
          }
        });
      }
    };
    
    // Store ref value to avoid stale closure
    const carousel = carouselRef.current;
    
    if (carousel) {
      // Use passive listener for better performance
      carousel.addEventListener('scroll', handleCarouselScroll, { passive: true });
      
      return () => {
        carousel.removeEventListener('scroll', handleCarouselScroll);
      };
    }
  }, [activePhoneIndex]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section 
      id="app-section" 
      ref={sectionRef}
      className={`app-section ${isScrollable ? 'scrollable' : ''}`}
      data-active-phone={activePhoneIndex}
    >
      <div className="app-container">
        <motion.div
          ref={ref}
          className="app-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.div className="app-text-content" variants={itemVariants}>
            <h2 className="app-title">Get in shape at the fastest possible rate!</h2>
            <p className="app-description">
            Achieve your goals with step-by-step guidance across fitness, nutrition, and every part of your journey.
            </p>
            
            <div className="app-features">
              <div className="feature">
                <div className="feature-icon">📊</div>
                <div className="feature-text">
                  <h4>Full Training Support</h4>
                  <p>Get a personalized program designed around your schedule, timezone, and gym setup—so you can train anytime, anywhere.</p>
                </div>
              </div>
              
              <div className="feature">
                <div className="feature-icon">🍎</div>
                <div className="feature-text">
                  <h4>Nutrition Coaching</h4>
                  <p>Receive tailored meal guidance that fits your lifestyle, with ongoing adjustments to keep you energized and on track.</p>
                </div>
              </div>
              
              <div className="feature">
                <div className="feature-icon">⏰</div>
                <div className="feature-text">
                  <h4>Lifestyle Guidance</h4>
                  <p>Benefit from step-by-step coaching beyond workouts and meals—covering habits, recovery, and everything you need to succeed.</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="app-mockups" 
            variants={itemVariants}
            ref={carouselRef}
          >
            {/* First phone: Dashboard screen */}
            <div className={`phone-mockup secondary-phone left ${activePhoneIndex === 0 ? 'active' : ''}`}>
              <div className="phone-frame">
                <div className="phone-screen">
                  <div className="status-bar">
                    <span className="time">15:14</span>
                    <div className="status-icons">
                      <span className="signal"></span>
                      <span className="wifi"></span>
                      <span className="battery"></span>
                    </div>
                  </div>
                  
                  <div className="dashboard-screen">
                    <div className="greeting">Hey Jenny!</div>
                    
                    <div className="getting-started-card">
                      <div className="card-icon"></div>
                      <div>
                        <h4>Getting started</h4>
                        <p>Explore the basics in the app<br />Learn more about the features</p>
                        <div className="progress-bar">
                          <div className="progress-fill"></div>
                        </div>
                        <span className="progress-text">0 of 4 completed</span>
                      </div>
                    </div>
                    
                    <div className="calendar-section">
                      <div className="calendar-header">Thursday, 13 April</div>
                      <div className="calendar-days">
                        <span>13</span><span>14</span><span>15</span>
                        <span className="selected">16</span><span>17</span><span>18</span>
                      </div>
                    </div>
                    
                    <div className="todo-section">
                      <h4>Things to do</h4>
                      <div className="todo-item">
                        <span className="todo-icon">🎾</span>
                        <div>
                          <span className="todo-title">Tennis</span>
                          <span className="todo-time">6:00 - 5:00</span>
                        </div>
                      </div>
                      <div className="todo-item">
                        <span className="todo-icon">🍎</span>
                        <div>
                          <span className="todo-title">Stretching</span>
                          <span className="todo-time">11:40 AM</span>
                        </div>
                        <span className="todo-check">✓</span>
                      </div>
                    </div>
                    
                    <div className="bottom-nav">
                      <div className="nav-item active">Dashboard</div>
                      <div className="nav-item">Log</div>
                      <div className="nav-item">Body</div>
                      <div className="nav-item">More</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Second phone: Main welcome screen (center) */}
            <div className={`phone-mockup main-phone ${activePhoneIndex === 1 ? 'active' : ''}`}>
              <div className="phone-frame">
                <div className="phone-screen">
                  <div className="status-bar">
                    <span className="time">11:11</span>
                    <div className="status-icons">
                      <span className="signal"></span>
                      <span className="wifi"></span>
                      <span className="battery"></span>
                    </div>
                  </div>
                  
                  <div className="app-content-screen">
                    <div className="app-logo">
                      <div className="logo-icon"></div>
                      <h3>Fit</h3>
                      <p>Stay in touch with your<br />Personal Trainer</p>

                    </div>
                    
                    <button 
                      className="app-get-started"
                      onClick={() => navigate('/professionals')}
                      >
                      Get a plan
                    </button>
                    <div className="home-indicator"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Third phone: Food diary screen */}
            <div className={`phone-mockup secondary-phone right ${activePhoneIndex === 2 ? 'active' : ''}`}>
              <div className="phone-frame">
                <div className="phone-screen">
                  <div className="status-bar">
                    <span className="time">15:14</span>
                    <div className="status-icons">
                      <span className="signal"></span>
                      <span className="wifi"></span>
                      <span className="battery"></span>
                    </div>
                  </div>
                  
                  <div className="food-diary-screen">
                    <div className="screen-header">
                      <span className="back-arrow">‹</span>
                      <h3>Food diary</h3>
                    </div>
                    
                    <div className="date-selector">Thursday, 13 April</div>
                    
                    <div className="calendar-pills">
                      <span>11</span><span>12</span><span>13</span>
                      <span className="selected">14</span><span>16</span><span>17</span>
                    </div>
                    
                    <div className="calories-display">
                      <span className="calories-number">1500</span>
                      <span className="calories-total">of 2,000 kcal</span>
                    </div>
                    
                    <div className="macros">
                      <div className="macro">
                        <span className="macro-label">Protein</span>
                        <span className="macro-value">79 g</span>
                      </div>
                      <div className="macro">
                        <span className="macro-label">Carbs</span>
                        <span className="macro-value">184 g/230 g</span>
                      </div>
                      <div className="macro">
                        <span className="macro-label">Fat</span>
                        <span className="macro-value">57 g/86 g</span>
                      </div>
                    </div>
                    
                    <div className="meals">
                      <div className="meal">
                        <h4>Breakfast</h4>
                        <span className="meal-calories">450 kcal</span>
                        <div className="meal-item">
                          <span className="meal-icon">🍞</span>
                          <span>Bread with egg and avocado</span>
                        </div>
                      </div>
                      
                      <div className="meal">
                        <h4>Lunch</h4>
                        <span className="meal-calories">740 kcal</span>
                        <div className="meal-item">
                          <span className="meal-icon">🍛</span>
                          <span>Serving of rice with curry veggie mix</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default PersonalAppSection;
